/**
 * Admin → Settings → Estimate output: the look of the results page's hero card
 * (components/results/cardStyle.ts) — its corners, background, border, shadow
 * and inner glow, the glass of the price box and the summary strip, and the
 * animation over a designed room's still while its first photoreal picture
 * renders — with the card itself drawn alongside from the draft, so every
 * change shows before it is saved.
 *
 * The preview is the real card's classes and components (the price panel, the
 * summary panel, the render dock with its progress and strip) over a stand-in
 * picture, so what is seen here is what the estimate page draws.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LayoutGrid, Layers, Ruler, PaintBucket, Grid2x2, Palette, RotateCcw, Save } from 'lucide-react';
import {
  DEFAULT_CARD_STYLE, WAIT_EFFECTS, cardStyleVars, type CardShadow, type EstimateCardStyle, type WaitEffect,
} from '../../components/results/cardStyle';
import {
  CORNERS, DEFAULT_DOWNLOAD_BUTTON, DEFAULT_WATERMARK, DEFAULT_WATERMARK_URL, watermarkCss, type DownloadButtonSettings, type WatermarkSettings,
} from '../../components/results/watermark';
import { DownloadButton } from '../../components/results/DownloadButton';
import { ImageUploadField } from '../../components/admin/ImageUploadField';
import { OverlayConfigPanel, OverlayPricePanel } from '../../components/results/ResultsVisualCard';
import { RenderGallery } from '../../components/results/render/RenderGallery';
import { RenderProgress, durationText } from '../../components/results/render/RenderProgress';
import { MosaicLayer } from '../../components/results/render/MosaicLayer';
import { PICTURE_ORDER, type RenderJob, type RenderPicture } from '../../components/results/render/types';
import type { DataPoint } from '../../components/results/configDataPoints';
import { useTranslation } from 'react-i18next';
import '../../components/results/ResultsVisualCard.css';
import '../../components/results/render/render.css';

// ── The preview ─────────────────────────────────────────────────────────────

type PreviewState = 'waiting' | 'done';

const MOCK_TOTALS = { totalCostUSD: 5420, totalCostCAD: 7400 };
const ROOM_POINTS: DataPoint[] = [
  { Icon: LayoutGrid, label: 'Application', value: 'Basement' },
  { Icon: Grid2x2, label: 'Project Type', value: 'Full Room' },
  { Icon: Ruler, label: 'Room Size', value: "16' L × 24' W × 8' H" },
  { Icon: Palette, label: 'Wall Color', value: 'White' },
];
const SLAT_POINTS: DataPoint[] = [
  { Icon: Palette, label: 'Color', value: 'Gray' },
  { Icon: Layers, label: 'Panels', value: '8 × 6" rows' },
  { Icon: Ruler, label: 'Height', value: '4.0 ft' },
];

const waitingJob: RenderJob = {
  id: 1, version: 1, status: 'rendering', queuePosition: 0, queueAhead: 0, percent: 24,
  picture: { id: 'view1', index: 2, count: 6, name: 'Corner view 1', percent: 15 }, stage: 'rendering', step: null, phase: 'loading',
  etaSec: 130, attempts: 1, error: null, createdAt: null, startedAt: null, finishedAt: null,
};
const doneJob: RenderJob = { ...waitingJob, status: 'done', percent: 100, picture: null, stage: 'done', phase: null, etaSec: 0, durationSec: 252 };

/** A real render of a designed room (alpha, 4 Nov 2026), shipped with the build: its quick still, its first picture and the six thumbnails. */
const SAMPLE = `${process.env.PUBLIC_URL || ''}/admin/card-preview`;
const SAMPLE_STILL = `${SAMPLE}/preview.jpg`;
const SAMPLE_PHOTO = `${SAMPLE}/user.jpg`;

const pictures = (state: PreviewState): RenderPicture[] => PICTURE_ORDER.map((id, i) => {
  const done = state === 'done' || i === 0;
  return { id, name: id, url: done ? SAMPLE_PHOTO : null, thumbUrl: done ? `${SAMPLE}/${id}-thumb.jpg` : null, width: 1920, height: 1080, done };
});

/** The hero card as the estimate page draws it, from a style, over a stand-in picture — with the watermark and the download button as set. */
export const EstimateCardPreview: React.FC<{ style: EstimateCardStyle; state: PreviewState; watermark?: WatermarkSettings; download?: DownloadButtonSettings }> = ({ style, state, watermark, download }) => {
  const { t } = useTranslation('results');
  const overlayRef = useRef<HTMLDivElement>(null);
  // The dock keeps clear of the strip by its measured height, as on the estimate page.
  useEffect(() => {
    const el = overlayRef.current;
    const card = el?.parentElement;
    if (!el || !card) return undefined;
    const measure = () => card.style.setProperty('--rvc-overlay-h', `${window.getComputedStyle(el).position === 'absolute' ? el.offsetHeight : 0}px`);
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);
  const waiting = state === 'waiting' && style.waitEffect !== 'none';
  const job = state === 'waiting' ? waitingJob : doneJob;
  const pics = useMemo(() => pictures(state), [state]);
  const renderedIn = state === 'done' ? durationText(t, doneJob.durationSec) : null;
  // The mosaic's run, stood in for: the first picture's nearness going 0 to 1 over 20 s, holding a moment at each end, over and over.
  const [mosaicProgress, setMosaicProgress] = useState(0);
  useEffect(() => {
    if (state !== 'waiting' || !style.mosaic) return undefined;
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const cycle = ((now - t0) / 1000) % 24;
      setMosaicProgress(cycle < 2 ? 0 : cycle > 22 ? 1 : (cycle - 2) / 20);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [state, style.mosaic]);
  return (
    <div className={`results-visual-card position-relative overflow-hidden is-styled ecp-card`} style={cardStyleVars(style)}>
      <div className="rvc-image-stage">
        <div className={`rstage${waiting ? ` rstage-wait-${style.waitEffect}` : ''}`}>
          <img className="rstage-img" src={state === 'done' ? SAMPLE_PHOTO : SAMPLE_STILL} alt={state === 'done' ? 'A rendered picture of a sample room' : 'The quick still of a sample room'} />
          {state === 'waiting' && style.mosaic && <MosaicLayer src={SAMPLE_STILL} progress={mosaicProgress} start={style.mosaicStart} end={style.mosaicEnd} />}
          {/* The watermark as the server will stamp it (a finished picture: the renders get it; the still never does). */}
          {watermark && watermark.enabled && watermark.onRenders && state === 'done' && (
            <img className="ecp-watermark" src={watermark.imageUrl || DEFAULT_WATERMARK_URL} alt="" style={{ ...watermarkCss(watermark), zIndex: 2, pointerEvents: 'none' }} />
          )}
          {download && download.onRenders && state === 'done' && (
            <DownloadButton settings={download} url={SAMPLE_PHOTO} project="Basement estimate" view="Your view" inert />
          )}
          {waiting && <div className="rstage-wait" aria-hidden="true" />}
          <div className="rdock">
            {state === 'waiting' && <RenderProgress job={job} done={1} total={pics.length} delayed={false} checking={false} />}
            {renderedIn && <div className="rdock-done">{t('render.progress.renderedIn', { time: renderedIn })}</div>}
            <RenderGallery pictures={pics} activeId={state === 'done' ? 'user' : 'preview'} renderingId={state === 'waiting' ? 'view1' : null} onPick={() => undefined} />
          </div>
        </div>
      </div>
      <div className="results-visual-overlay" ref={overlayRef}>
        <div className="d-flex h-100">
          <div className="overlay-col-price" style={{ flex: '0 0 25%' }}>
            <OverlayPricePanel totals={MOCK_TOTALS} currency="CAD" pulseKey={0} totalFormat="rounded" />
          </div>
          <div className="overlay-col-room" style={{ flex: '0 0 37.5%' }}>
            <OverlayConfigPanel title={t('pdf.cards.room')} TitleIcon={LayoutGrid} dataPoints={ROOM_POINTS} />
          </div>
          <div className="overlay-col-slat" style={{ flex: '0 0 37.5%' }}>
            <OverlayConfigPanel title={t('pdf.cards.slatWall')} TitleIcon={Layers} dataPoints={SLAT_POINTS} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ── The controls ────────────────────────────────────────────────────────────

const Row: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div className="d-flex align-items-center gap-2 mb-2" title={hint}>
    <span className="small fw-semibold" style={{ flex: '0 0 118px' }}>{label}</span>
    <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">{children}</div>
  </div>
);

const Range: React.FC<{ value: number; min: number; max: number; step: number; unit?: string; onChange: (v: number) => void; label: string }> = ({ value, min, max, step, unit, onChange, label }) => (
  <>
    <input type="range" className="form-range flex-grow-1" min={min} max={max} step={step} value={value} aria-label={label} onChange={(e) => onChange(parseFloat(e.target.value))} />
    <span className="badge text-bg-light" style={{ minWidth: 58, fontVariantNumeric: 'tabular-nums' }}>{Number.isInteger(step) ? Math.round(value) : value.toFixed(2)}{unit ?? ''}</span>
  </>
);

const Colour: React.FC<{ value: string; onChange: (v: string) => void; label: string }> = ({ value, onChange, label }) => (
  <>
    <input type="color" className="form-control form-control-color p-0" style={{ width: 36, height: 28 }} value={value} aria-label={label} onChange={(e) => onChange(e.target.value.toLowerCase())} />
    <code className="small text-muted">{value}</code>
  </>
);

export interface EstimateCardStyleSectionProps {
  value: EstimateCardStyle;
  watermark: WatermarkSettings;
  download: DownloadButtonSettings;
  saving: boolean;
  onSave: (patch: { estimateCardStyle: EstimateCardStyle; watermark: WatermarkSettings; downloadButton: DownloadButtonSettings }) => Promise<void> | void;
}

const Corners: React.FC<{ value: string; name: string; onChange: (c: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right') => void }> = ({ value, name, onChange }) => (
  <div className="d-flex flex-wrap gap-2">
    {CORNERS.map((c) => (
      <label key={c.id} className="d-flex align-items-center gap-1 small" style={{ cursor: 'pointer' }}>
        <input type="radio" className="form-check-input m-0" name={name} checked={value === c.id} onChange={() => onChange(c.id)} />
        {c.label}
      </label>
    ))}
  </div>
);

export const EstimateCardStyleSection: React.FC<EstimateCardStyleSectionProps> = ({ value, watermark, download, saving, onSave }) => {
  const [draft, setDraft] = useState<EstimateCardStyle>(value);
  const [wm, setWm] = useState<WatermarkSettings>(watermark);
  const [dl, setDl] = useState<DownloadButtonSettings>(download);
  const [previewState, setPreviewState] = useState<PreviewState>('waiting');
  const dirty = JSON.stringify(draft) !== JSON.stringify(value) || JSON.stringify(wm) !== JSON.stringify(watermark) || JSON.stringify(dl) !== JSON.stringify(download);
  // A save elsewhere (another tab) lands here unless there is unsaved work.
  const dirtyRef = useRef(dirty); dirtyRef.current = dirty;
  useEffect(() => { if (!dirtyRef.current) setDraft(value); }, [value]);
  useEffect(() => { if (!dirtyRef.current) setWm(watermark); }, [watermark]);
  useEffect(() => { if (!dirtyRef.current) setDl(download); }, [download]);
  const set = <K extends keyof EstimateCardStyle>(key: K, v: EstimateCardStyle[K]) => setDraft((d) => ({ ...d, [key]: v }));
  const setShadow = <K extends keyof CardShadow>(key: K, v: CardShadow[K]) => setDraft((d) => ({ ...d, shadow: { ...d.shadow, [key]: v } }));
  const setW = <K extends keyof WatermarkSettings>(key: K, v: WatermarkSettings[K]) => setWm((d) => ({ ...d, [key]: v }));
  const setD = <K extends keyof DownloadButtonSettings>(key: K, v: DownloadButtonSettings[K]) => setDl((d) => ({ ...d, [key]: v }));
  const save = () => onSave({ estimateCardStyle: draft, watermark: wm, downloadButton: dl });
  const revert = () => { setDraft(value); setWm(watermark); setDl(download); };
  const factory = () => { setDraft(DEFAULT_CARD_STYLE); setWm(DEFAULT_WATERMARK); setDl(DEFAULT_DOWNLOAD_BUTTON); };

  return (
    <div className="settings-group border rounded-3 p-3 mb-3">
      <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2"><PaintBucket size={18} /> Estimate output card</h5>
      <p className="text-muted small mb-3">
        The hero card at the top of a finished estimate: its corners, border, shadow and glow, the glass of the
        price box and the summary strip, and — for a room from the 3D designer — what plays over the quick
        still while the first photoreal picture renders. The card on the right is drawn from these controls as
        you move them; nothing reaches the site until Save.
      </p>

      <div className="row g-4">
        <div className="col-12 col-xl-5">
          <h6 className="fw-semibold small text-uppercase text-muted mb-2">Where it applies</h6>
          <div className="d-flex flex-column gap-1 mb-3">
            {([['advanced', 'Designed rooms only (the advanced output)', 'The cards of the three classic estimate types keep their present look.'], ['all', 'Every estimate', 'The same card for the classic estimates and designed rooms alike.']] as const).map(([id, label, note]) => (
              <label key={id} className={`border rounded-3 p-2 ${draft.scope === id ? 'border-primary' : 'border-light-subtle'}`} style={{ cursor: 'pointer' }}>
                <div className="d-flex align-items-center gap-2">
                  <input type="radio" className="form-check-input m-0" name="ecard-scope" checked={draft.scope === id} onChange={() => set('scope', id)} />
                  <span className="fw-semibold small">{label}</span>
                </div>
                <div className="text-muted small ms-4">{note}</div>
              </label>
            ))}
          </div>

          <h6 className="fw-semibold small text-uppercase text-muted mb-2">The card</h6>
          <Row label="Corner radius"><Range label="Corner radius" value={draft.radius} min={0} max={48} step={1} unit="px" onChange={(v) => set('radius', v)} /></Row>
          <Row label="Background" hint="Seen at the card's edges and behind a letterboxed picture."><Colour label="Card background" value={draft.background} onChange={(v) => set('background', v)} /></Row>
          <Row label="Picture area" hint="Behind the picture itself while it loads."><Colour label="Picture area background" value={draft.stageBackground} onChange={(v) => set('stageBackground', v)} /></Row>
          <Row label="Border width"><Range label="Border width" value={draft.borderWidth} min={0} max={12} step={0.5} unit="px" onChange={(v) => set('borderWidth', v)} /></Row>
          <Row label="Border colour"><Colour label="Border colour" value={draft.borderColor} onChange={(v) => set('borderColor', v)} /></Row>
          <Row label="Border opacity"><Range label="Border opacity" value={draft.borderOpacity} min={0} max={1} step={0.01} onChange={(v) => set('borderOpacity', v)} /></Row>

          <h6 className="fw-semibold small text-uppercase text-muted mb-2 mt-3">Shadow</h6>
          <Row label="Offset across"><Range label="Shadow offset across" value={draft.shadow.x} min={-40} max={40} step={1} unit="px" onChange={(v) => setShadow('x', v)} /></Row>
          <Row label="Offset down"><Range label="Shadow offset down" value={draft.shadow.y} min={-40} max={40} step={1} unit="px" onChange={(v) => setShadow('y', v)} /></Row>
          <Row label="Blur"><Range label="Shadow blur" value={draft.shadow.blur} min={0} max={80} step={1} unit="px" onChange={(v) => setShadow('blur', v)} /></Row>
          <Row label="Spread"><Range label="Shadow spread" value={draft.shadow.spread} min={-20} max={40} step={1} unit="px" onChange={(v) => setShadow('spread', v)} /></Row>
          <Row label="Colour"><Colour label="Shadow colour" value={draft.shadow.color} onChange={(v) => setShadow('color', v)} /></Row>
          <Row label="Opacity"><Range label="Shadow opacity" value={draft.shadow.opacity} min={0} max={1} step={0.01} onChange={(v) => setShadow('opacity', v)} /></Row>

          <h6 className="fw-semibold small text-uppercase text-muted mb-2 mt-3">Inner glow</h6>
          <Row label="Size" hint="The soft light just inside the card's edge. 0 turns it off."><Range label="Inner glow size" value={draft.glowSize} min={0} max={40} step={1} unit="px" onChange={(v) => set('glowSize', v)} /></Row>
          <Row label="Colour"><Colour label="Inner glow colour" value={draft.glowColor} onChange={(v) => set('glowColor', v)} /></Row>
          <Row label="Opacity"><Range label="Inner glow opacity" value={draft.glowOpacity} min={0} max={1} step={0.01} onChange={(v) => set('glowOpacity', v)} /></Row>

          <h6 className="fw-semibold small text-uppercase text-muted mb-2 mt-3">Price box</h6>
          <Row label="Colour" hint="A partner portal's own slate colour still wins; the opacity and blur are these."><Colour label="Price box colour" value={draft.priceBg} onChange={(v) => set('priceBg', v)} /></Row>
          <Row label="Opacity"><Range label="Price box opacity" value={draft.priceOpacity} min={0} max={1} step={0.01} onChange={(v) => set('priceOpacity', v)} /></Row>
          <Row label="Blur behind" hint="How much the picture behind the box is blurred through it."><Range label="Price box blur" value={draft.priceBlur} min={0} max={40} step={1} unit="px" onChange={(v) => set('priceBlur', v)} /></Row>

          <h6 className="fw-semibold small text-uppercase text-muted mb-2 mt-3">Summary strip</h6>
          <Row label="Colour"><Colour label="Summary strip colour" value={draft.summaryBg} onChange={(v) => set('summaryBg', v)} /></Row>
          <Row label="Opacity"><Range label="Summary strip opacity" value={draft.summaryOpacity} min={0} max={1} step={0.01} onChange={(v) => set('summaryOpacity', v)} /></Row>
          <Row label="Blur behind"><Range label="Summary strip blur" value={draft.summaryBlur} min={0} max={40} step={1} unit="px" onChange={(v) => set('summaryBlur', v)} /></Row>

          <h6 className="fw-semibold small text-uppercase text-muted mb-2 mt-3">Render progress box</h6>
          <p className="text-muted small mb-2">The box over the picture with the progress line and the strip of thumbnails (designed rooms).</p>
          <Row label="Colour"><Colour label="Progress box colour" value={draft.dockBg} onChange={(v) => set('dockBg', v)} /></Row>
          <Row label="Opacity"><Range label="Progress box opacity" value={draft.dockOpacity} min={0} max={1} step={0.01} onChange={(v) => set('dockOpacity', v)} /></Row>
          <Row label="Blur behind"><Range label="Progress box blur" value={draft.dockBlur} min={0} max={40} step={1} unit="px" onChange={(v) => set('dockBlur', v)} /></Row>
          <Row label="Corner radius"><Range label="Progress box corner radius" value={draft.dockRadius} min={0} max={40} step={1} unit="px" onChange={(v) => set('dockRadius', v)} /></Row>
          <Row label="Border colour"><Colour label="Progress box border colour" value={draft.dockBorderColor} onChange={(v) => set('dockBorderColor', v)} /></Row>
          <Row label="Border opacity" hint="0 is no border."><Range label="Progress box border opacity" value={draft.dockBorderOpacity} min={0} max={1} step={0.01} onChange={(v) => set('dockBorderOpacity', v)} /></Row>
          <Row label="Accent" hint="The progress bar, the ring round the picture shown, and the pulse over the one rendering."><Colour label="Accent colour" value={draft.accent} onChange={(v) => set('accent', v)} /></Row>

          <h6 className="fw-semibold small text-uppercase text-muted mb-2 mt-3">Thumbnails</h6>
          <Row label="Colour" hint="The box of a picture still to come (a finished one shows its own thumbnail)."><Colour label="Thumbnail colour" value={draft.thumbBg} onChange={(v) => set('thumbBg', v)} /></Row>
          <Row label="Opacity"><Range label="Thumbnail opacity" value={draft.thumbOpacity} min={0} max={1} step={0.01} onChange={(v) => set('thumbOpacity', v)} /></Row>
          <Row label="Corner radius"><Range label="Thumbnail corner radius" value={draft.thumbRadius} min={0} max={30} step={1} unit="px" onChange={(v) => set('thumbRadius', v)} /></Row>
          <Row label="Outline colour"><Colour label="Thumbnail outline colour" value={draft.thumbBorderColor} onChange={(v) => set('thumbBorderColor', v)} /></Row>
          <Row label="Outline opacity"><Range label="Thumbnail outline opacity" value={draft.thumbBorderOpacity} min={0} max={1} step={0.01} onChange={(v) => set('thumbBorderOpacity', v)} /></Row>

          <h6 className="fw-semibold small text-uppercase text-muted mb-2 mt-3">While the first photoreal picture renders</h6>
          <p className="text-muted small mb-2">Designed rooms only. Plays over the quick still from the moment the estimate is saved until the first picture takes its place.</p>
          <div className="row g-2 mb-3">
            {WAIT_EFFECTS.map((w) => (
              <div key={w.id} className="col-6">
                <label className={`d-block border rounded-3 p-2 h-100 ${draft.waitEffect === w.id ? 'border-primary shadow-sm' : 'border-light-subtle'}`} style={{ cursor: 'pointer', background: draft.waitEffect === w.id ? 'rgba(13,110,253,0.04)' : '#fff' }}>
                  <div className="d-flex align-items-center gap-2">
                    <input type="radio" className="form-check-input m-0" name="ecard-wait" checked={draft.waitEffect === w.id} onChange={() => { set('waitEffect', w.id as WaitEffect); setPreviewState('waiting'); }} />
                    <span className="fw-semibold small">{w.label}</span>
                  </div>
                  <div className="text-muted small">{w.description}</div>
                </label>
              </div>
            ))}
          </div>

          <label className="d-flex align-items-center gap-2 mb-2" style={{ cursor: 'pointer' }}>
            <input type="checkbox" className="form-check-input m-0" checked={draft.mosaic} onChange={(e) => { set('mosaic', e.target.checked); setPreviewState('waiting'); }} />
            <span className="fw-semibold small">Mosaic</span>
            <span className="text-muted small">The still in blocks, coarse at first and finer as the first picture nears. Plays under whichever effect is chosen above.</span>
          </label>
          {draft.mosaic && (
            <>
              <Row label="Starting block" hint="How coarse the mosaic is the moment the estimate is saved."><Range label="Mosaic starting block" value={draft.mosaicStart} min={2} max={200} step={1} unit="px" onChange={(v) => set('mosaicStart', v)} /></Row>
              <Row label="Finishing block" hint="How fine it is just before the first picture lands. 1 is the still itself."><Range label="Mosaic finishing block" value={draft.mosaicEnd} min={1} max={100} step={1} unit="px" onChange={(v) => set('mosaicEnd', v)} /></Row>
              <p className="text-muted small">On the estimate page the blocks follow the render's own progress — the setup steps, then the first picture's percent — so they are at the finishing size as the picture arrives. The preview runs the whole way in 20 seconds.</p>
            </>
          )}

          <h6 className="fw-semibold small text-uppercase text-muted mb-2 mt-3">Picture watermark</h6>
          <p className="text-muted small mb-2">Stamped into the picture files on the server: the layered visualizer's composites, and a designed room's photoreal pictures as they land (never the 360). A partner portal's own mark, set on the retailer, takes the default's place.</p>
          <label className="d-flex align-items-center gap-2 mb-2" style={{ cursor: 'pointer' }}>
            <input type="checkbox" className="form-check-input m-0" checked={wm.enabled} onChange={(e) => setW('enabled', e.target.checked)} />
            <span className="fw-semibold small">Watermark the pictures</span>
          </label>
          <div className="d-flex flex-wrap gap-3 mb-2">
            <label className="d-flex align-items-center gap-2 small" style={{ cursor: 'pointer' }}>
              <input type="checkbox" className="form-check-input m-0" checked={wm.onStatic} disabled={!wm.enabled} onChange={(e) => setW('onStatic', e.target.checked)} />
              The layered visualizer (classic estimates)
            </label>
            <label className="d-flex align-items-center gap-2 small" style={{ cursor: 'pointer' }}>
              <input type="checkbox" className="form-check-input m-0" checked={wm.onRenders} disabled={!wm.enabled} onChange={(e) => { setW('onRenders', e.target.checked); setPreviewState('done'); }} />
              Photoreal renders (designed rooms)
            </label>
          </div>
          <div className="mb-2">
            <label className="form-label small fw-semibold mb-1">Watermark image</label>
            <ImageUploadField
              value={wm.imageUrl || null} onChange={(v) => setW('imageUrl', v ?? '')} retailerId={0} kind="watermark" allowUniversalUpload
              aspectPresets={[{ label: 'Free', aspect: undefined }]} placeholder={DEFAULT_WATERMARK_URL}
              helpText="A PNG with transparency. Empty: the built-in Trusscore mark. Upload a new one and the next pictures carry it; pictures already made keep theirs."
              previewBg="#5a6b7c" disabled={!wm.enabled}
            />
          </div>
          <Row label="Size" hint="Its width as a share of the picture's width."><Range label="Watermark size" value={wm.sizePct} min={2} max={50} step={0.5} unit="%" onChange={(v) => setW('sizePct', v)} /></Row>
          <Row label="Opacity"><Range label="Watermark opacity" value={wm.opacity} min={0} max={1} step={0.01} onChange={(v) => setW('opacity', v)} /></Row>
          <Row label="Corner"><Corners name="ecard-wm-corner" value={wm.position} onChange={(c) => setW('position', c)} /></Row>
          <Row label="Margin" hint="From the edges, in pixels on a 1920-wide picture; scaled with the picture."><Range label="Watermark margin" value={wm.margin} min={0} max={200} step={1} unit="px" onChange={(v) => setW('margin', v)} /></Row>

          <h6 className="fw-semibold small text-uppercase text-muted mb-2 mt-3">Download button</h6>
          <p className="text-muted small mb-2">An icon over the picture that saves the one shown, named after the project and the view. Never over the 360.</p>
          <label className="d-flex align-items-center gap-2 mb-2" style={{ cursor: 'pointer' }}>
            <input type="checkbox" className="form-check-input m-0" checked={dl.enabled} onChange={(e) => setD('enabled', e.target.checked)} />
            <span className="fw-semibold small">Show the download button</span>
          </label>
          <div className="d-flex flex-wrap gap-3 mb-2">
            <label className="d-flex align-items-center gap-2 small" style={{ cursor: 'pointer' }}>
              <input type="checkbox" className="form-check-input m-0" checked={dl.onStatic} disabled={!dl.enabled} onChange={(e) => setD('onStatic', e.target.checked)} />
              The layered visualizer (classic estimates)
            </label>
            <label className="d-flex align-items-center gap-2 small" style={{ cursor: 'pointer' }}>
              <input type="checkbox" className="form-check-input m-0" checked={dl.onRenders} disabled={!dl.enabled} onChange={(e) => setD('onRenders', e.target.checked)} />
              Designed rooms (the renders, once the first is in)
            </label>
          </div>
          <Row label="Corner"><Corners name="ecard-dl-corner" value={dl.position} onChange={(c) => setD('position', c)} /></Row>
          <Row label="Size"><Range label="Download button size" value={dl.size} min={20} max={72} step={1} unit="px" onChange={(v) => setD('size', v)} /></Row>
          <Row label="Colour"><Colour label="Download button colour" value={dl.color} onChange={(v) => setD('color', v)} /></Row>
          <Row label="Opacity" hint="At rest; it is fully opaque under the pointer."><Range label="Download button opacity" value={dl.opacity} min={0} max={1} step={0.01} onChange={(v) => setD('opacity', v)} /></Row>

          <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
            <button type="button" className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1" disabled={!dirty || saving} onClick={save}>
              <Save size={14} /> {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={!dirty || saving} onClick={revert}>Revert</button>
            <button type="button" className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1" disabled={saving} onClick={factory}>
              <RotateCcw size={14} /> Factory look
            </button>
            {dirty && <span className="text-muted small">Unsaved changes — the preview shows them.</span>}
          </div>
        </div>

        <div className="col-12 col-xl-7">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h6 className="fw-semibold small text-uppercase text-muted m-0">Live preview</h6>
            <div className="btn-group btn-group-sm" role="group" aria-label="Preview state">
              <button type="button" className={`btn ${previewState === 'waiting' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setPreviewState('waiting')}>Waiting for the render</button>
              <button type="button" className={`btn ${previewState === 'done' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setPreviewState('done')}>Pictures in</button>
            </div>
          </div>
          <div className="ecp-wrap">
            <EstimateCardPreview style={draft} state={previewState} watermark={wm} download={dl} />
          </div>
          <p className="text-muted small mt-2 mb-0">
            The room is a real render of a designed basement (its quick still, then its first picture); the price and the configuration are stand-ins. The card, its panels, the progress line and the strip of thumbnails are the estimate page's own.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EstimateCardStyleSection;
